import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchEFTFundTransaction from '@/hooks/useApi/useFetchEFTFundTransaction';
import EftFundingTransactionInfo from './EftFundingTransactionInfo';
import LinkedTransactionInfo from './LinkedTransactionInfo';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { isPaymentSubTransaction } from './isPaymentSubTransaction';

type EftFundingTransactionProps = {
  TransactionID: string;
} & Omit<
  ComponentProps<typeof EftFundingTransactionInfo>,
  'transaction' | 'subTransactionId' | 'subTransactionType' | 'subTransactionStatus'
>;

export default function EftFundingTransactionContainer({
  TransactionID,
  notes,
  isParent = false,
  jwt,
  eLinxRequestID = '',
  ...props
}: EftFundingTransactionProps) {
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { userSettings } = useUserSettingsProvider();

  const { eftFundTransaction, fetchEftFundTransaction, resetEftFundTransaction } = useFetchEFTFundTransaction({
    bearer: jwt,
  });

  useEffect(() => {
    blockingLoader.show();
    resetEftFundTransaction();

    fetchEftFundTransaction({ TransactionID, Timezone: userSettings.timezone })
      .catch((error) =>
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred')),
      )
      .finally(() => blockingLoader.hide());
  }, [TransactionID]);

  const transaction = eftFundTransaction;

  const subTransactions = eftFundTransaction?.SubTransactions
    ? Object.values(eftFundTransaction.SubTransactions)
    : undefined;

  // A funding transaction can fund a payment on any rail, not just its own — don't assume the
  // child is an EFT Withdrawal, just skip Fee (Credit Card funding creates a sibling Fee child).
  const withdrawalSubTransaction = subTransactions?.find(isPaymentSubTransaction);

  return (
    <div>
      {transaction && (
        <EftFundingTransactionInfo
          transaction={transaction}
          notes={notes}
          isParent={isParent}
          jwt={jwt}
          eLinxRequestID={eLinxRequestID}
          subTransactionId={withdrawalSubTransaction?.TransactionID}
          subTransactionType={withdrawalSubTransaction?.TransactionType}
          subTransactionStatus={withdrawalSubTransaction?.TransactionStatus}
          {...props}
        />
      )}

      {subTransactions && subTransactions.length > 0 && (
        <LinkedTransactionInfo LinkedTransactions={subTransactions} Currency={transaction?.Currency || 'CAD'} />
      )}
    </div>
  );
}
